import React, { useState } from 'react';
import { Settings, Camera, Bell, Shield, Database, Wifi, Monitor, Zap } from 'lucide-react';
import { AIChatAssistant } from '../components/AIChatAssistant';
export function SettingsPage() {
  const [notifications, setNotifications] = useState(true);
  const [soundAlerts, setSoundAlerts] = useState(true);
  const [autoScan, setAutoScan] = useState(false);
  const [sensitivity, setSensitivity] = useState(85);
  const [threshold, setThreshold] = useState(95);
  const [volume, setVolume] = useState(70);
  return <div className="p-6 max-w-6xl mx-auto bg-background min-h-screen" data-id="element-584">
      <div className="mb-8" data-id="element-585">
        <h1 className="text-3xl font-bold text-text-primary mb-2" data-id="element-586">
          System Settings
        </h1>
        <p className="text-text-secondary" data-id="element-587">
          Configure device, alerts, and system preferences
        </p>
      </div>

      <div className="grid grid-cols-2 gap-6" data-id="element-588">
        {/* Camera & Detection */}
        <div className="bg-surface rounded-card border border-border p-6" data-id="element-589">
          <h3 className="text-lg font-bold text-text-primary mb-6 flex items-center gap-2" data-id="element-590">
            <Camera className="w-5 h-5 text-info" data-id="element-591" />
            Camera & Detection
          </h3>
          <div className="space-y-6" data-id="element-592">
            <div data-id="element-593">
              <label className="block text-sm font-bold text-text-primary mb-2 uppercase tracking-wide" data-id="element-594">
                Camera Device
              </label>
              <select className="w-full bg-surface-2 border border-border rounded-input px-4 py-2.5 text-text-primary focus:ring-2 focus:ring-info" data-id="element-595">
                <option data-id="element-596">CAM-A (Primary Overhead)</option>
                <option data-id="element-597">CAM-B (Secondary Angle)</option>
                <option data-id="element-598">CAM-C (Mobile Unit)</option>
              </select>
            </div>

            <div data-id="element-599">
              <label className="block text-sm font-bold text-text-primary mb-2 uppercase tracking-wide" data-id="element-600">
                Detection Sensitivity: {sensitivity}%
              </label>
              <input type="range" min="0" max="100" value={sensitivity} onChange={e => setSensitivity(Number(e.target.value))} className="w-full accent-info" data-id="element-601" />
              <div className="flex justify-between text-xs text-text-secondary mt-1" data-id="element-602">
                <span data-id="element-603">Permissive (70-80%)</span>
                <span data-id="element-604">Balanced (85-90%)</span>
                <span data-id="element-605">Strict (95-100%)</span>
              </div>
            </div>

            <div data-id="element-606">
              <label className="block text-sm font-bold text-text-primary mb-2 uppercase tracking-wide" data-id="element-607">
                Confidence Threshold: {threshold}%
              </label>
              <input type="range" min="0" max="100" value={threshold} onChange={e => setThreshold(Number(e.target.value))} className="w-full accent-success" data-id="element-608" />
              <div className="flex justify-between text-xs text-text-secondary mt-1" data-id="element-609">
                <span data-id="element-610">Low</span>
                <span className={threshold >= 95 ? 'text-success font-bold' : ''} data-id="element-611">
                  Recommended: 95%
                </span>
                <span data-id="element-612">High</span>
              </div>
            </div>

            <div className="flex items-center justify-between p-4 bg-surface-2 rounded-input border border-border" data-id="element-613">
              <div data-id="element-614">
                <div className="text-sm font-medium text-text-primary" data-id="element-615">
                  Auto-scan on tray placement
                </div>
                <div className="text-xs text-text-secondary" data-id="element-616">
                  Trigger scan when tray is detected
                </div>
              </div>
              <button onClick={() => setAutoScan(!autoScan)} className={`relative w-12 h-6 rounded-input transition-colors ${autoScan ? 'bg-info' : 'bg-surface-2 border border-border'}`} data-id="element-617">
                <div className={`absolute top-0.5 left-0.5 w-5 h-5 bg-white rounded-input transition-transform ${autoScan ? 'translate-x-6' : ''}`} data-id="element-618" />
              </button>
            </div>

            <button className="w-full px-4 py-2.5 bg-info hover:bg-blue-500 text-white rounded-input font-bold transition-colors" data-id="element-619">
              Calibrate Camera
            </button>
          </div>
        </div>

        {/* Alerts & Notifications */}
        <div className="bg-surface rounded-card border border-border p-6" data-id="element-620">
          <h3 className="text-lg font-bold text-text-primary mb-6 flex items-center gap-2" data-id="element-621">
            <Bell className="w-5 h-5 text-warning" data-id="element-622" />
            Alerts & Notifications
          </h3>
          <div className="space-y-4" data-id="element-623">
            <div className="flex items-center justify-between p-4 bg-surface-2 rounded-input border border-border" data-id="element-624">
              <div data-id="element-625">
                <div className="text-sm font-medium text-text-primary" data-id="element-626">
                  Push Notifications
                </div>
                <div className="text-xs text-text-secondary" data-id="element-627">
                  Receive alerts for critical events
                </div>
              </div>
              <button onClick={() => setNotifications(!notifications)} className={`relative w-12 h-6 rounded-input transition-colors ${notifications ? 'bg-info' : 'bg-surface-2 border border-border'}`} data-id="element-628">
                <div className={`absolute top-0.5 left-0.5 w-5 h-5 bg-white rounded-input transition-transform ${notifications ? 'translate-x-6' : ''}`} data-id="element-629" />
              </button>
            </div>

            <div className="flex items-center justify-between p-4 bg-surface-2 rounded-input border border-border" data-id="element-630">
              <div data-id="element-631">
                <div className="text-sm font-medium text-text-primary" data-id="element-632">
                  Sound Alerts
                </div>
                <div className="text-xs text-text-secondary" data-id="element-633">
                  Audible warning for missing items
                </div>
              </div>
              <button onClick={() => setSoundAlerts(!soundAlerts)} className={`relative w-12 h-6 rounded-input transition-colors ${soundAlerts ? 'bg-info' : 'bg-surface-2 border border-border'}`} data-id="element-634">
                <div className={`absolute top-0.5 left-0.5 w-5 h-5 bg-white rounded-input transition-transform ${soundAlerts ? 'translate-x-6' : ''}`} data-id="element-635" />
              </button>
            </div>

            <div data-id="element-636">
              <label className="block text-sm font-bold text-text-primary mb-2 uppercase tracking-wide" data-id="element-637">
                Alert Volume: {volume}%
              </label>
              <input type="range" min="0" max="100" value={volume} onChange={e => setVolume(Number(e.target.value))} className="w-full accent-warning" data-id="element-638" />
              <div className="flex justify-between text-xs text-text-secondary mt-1" data-id="element-639">
                <span data-id="element-640">Silent</span>
                <span className="text-warning font-bold" data-id="element-641">{volume}%</span>
                <span data-id="element-642">Max</span>
              </div>
            </div>

            <div data-id="element-643">
              <label className="block text-sm font-bold text-text-primary mb-2 uppercase tracking-wide" data-id="element-644">
                Alert Escalation
              </label>
              <select className="w-full bg-surface-2 border border-border rounded-input px-4 py-2.5 text-text-primary focus:ring-2 focus:ring-info" data-id="element-645">
                <option data-id="element-646">Immediate (0 seconds)</option>
                <option data-id="element-647">After 30 seconds</option>
                <option data-id="element-648">After 1 minute</option>
                <option data-id="element-649">Manual only</option>
              </select>
            </div>

            <button className="w-full px-4 py-2.5 bg-warning hover:bg-yellow-500 text-white rounded-input font-bold transition-colors" data-id="element-650">
              Test Alert Sound
            </button>
          </div>
        </div>

        {/* Security */}
        <div className="bg-surface rounded-card border border-border p-6" data-id="element-651">
          <h3 className="text-lg font-bold text-text-primary mb-6 flex items-center gap-2" data-id="element-652">
            <Shield className="w-5 h-5 text-success" data-id="element-653" />
            Security & Access
          </h3>
          <div className="space-y-4" data-id="element-654">
            <div data-id="element-655">
              <label className="block text-sm font-bold text-text-primary mb-2 uppercase tracking-wide" data-id="element-656">
                Session Timeout
              </label>
              <select className="w-full bg-surface-2 border border-border rounded-input px-4 py-2.5 text-text-primary focus:ring-2 focus:ring-info" data-id="element-657">
                <option data-id="element-658">15 minutes</option>
                <option data-id="element-659">30 minutes</option>
                <option data-id="element-660">1 hour</option>
                <option data-id="element-661">Never</option>
              </select>
            </div>

            <div data-id="element-662">
              <label className="block text-sm font-bold text-text-primary mb-2 uppercase tracking-wide" data-id="element-663">
                Override Authorization
              </label>
              <select className="w-full bg-surface-2 border border-border rounded-input px-4 py-2.5 text-text-primary focus:ring-2 focus:ring-info" data-id="element-664">
                <option data-id="element-665">Require witness verification</option>
                <option data-id="element-666">Require supervisor approval</option>
                <option data-id="element-667">Require both</option>
              </select>
            </div>

            <button className="w-full px-4 py-2.5 bg-surface-2 hover:bg-surface text-text-primary border border-border rounded-input font-bold transition-colors" data-id="element-668">
              Change Password
            </button>
          </div>
        </div>

        {/* System Info */}
        <div className="bg-surface rounded-card border border-border p-6" data-id="element-669">
          <h3 className="text-lg font-bold text-text-primary mb-6 flex items-center gap-2" data-id="element-670">
            <Monitor className="w-5 h-5 text-info" data-id="element-671" />
            System Information
          </h3>
          <div className="space-y-4" data-id="element-672">
            <div className="flex items-center justify-between p-3 bg-surface-2 rounded-input border border-border" data-id="element-673">
              <div className="flex items-center gap-3" data-id="element-674">
                <Zap className="w-5 h-5 text-success" data-id="element-675" />
                <div data-id="element-676">
                  <div className="text-sm font-medium text-text-primary" data-id="element-677">
                    System Status
                  </div>
                  <div className="text-xs text-text-secondary" data-id="element-678">
                    All systems operational
                  </div>
                </div>
              </div>
              <div className="w-2 h-2 bg-success rounded-full animate-pulse" data-id="element-679"></div>
            </div>

            <div className="flex items-center justify-between p-3 bg-surface-2 rounded-input border border-border" data-id="element-680">
              <div className="flex items-center gap-3" data-id="element-681">
                <Wifi className="w-5 h-5 text-info" data-id="element-682" />
                <div data-id="element-683">
                  <div className="text-sm font-medium text-text-primary" data-id="element-684">
                    Network
                  </div>
                  <div className="text-xs text-text-secondary" data-id="element-685">
                    Connected • 98ms latency
                  </div>
                </div>
              </div>
              <div className="w-2 h-2 bg-success rounded-full" data-id="element-686"></div>
            </div>

            <div className="flex items-center justify-between p-3 bg-surface-2 rounded-input border border-border" data-id="element-687">
              <div className="flex items-center gap-3" data-id="element-688">
                <Database className="w-5 h-5 text-warning" data-id="element-689" />
                <div data-id="element-690">
                  <div className="text-sm font-medium text-text-primary" data-id="element-691">
                    Database
                  </div>
                  <div className="text-xs text-text-secondary" data-id="element-692">
                    Synced 2 minutes ago
                  </div>
                </div>
              </div>
              <div className="w-2 h-2 bg-success rounded-full" data-id="element-693"></div>
            </div>

            <div className="pt-4 border-t border-border space-y-2 text-sm" data-id="element-694">
              <div className="flex justify-between" data-id="element-695">
                <span className="text-text-secondary" data-id="element-696">Software Version</span>
                <span className="text-text-primary font-mono" data-id="element-697">v2.4.1</span>
              </div>
              <div className="flex justify-between" data-id="element-698">
                <span className="text-text-secondary" data-id="element-699">Last Updated</span>
                <span className="text-text-primary" data-id="element-700">Jan 10, 2024</span>
              </div>
              <div className="flex justify-between" data-id="element-701">
                <span className="text-text-secondary" data-id="element-702">Device ID</span>
                <span className="text-text-primary font-mono" data-id="element-703">OR4-CAM-A</span>
              </div>
            </div>

            <button className="w-full px-4 py-2.5 bg-info hover:bg-blue-500 text-white rounded-input font-bold transition-colors" data-id="element-704">
              Check for Updates
            </button>
          </div>
        </div>
      </div>

      {/* AI Chat Assistant */}
      <AIChatAssistant context="settings" data-id="element-705" />
    </div>;
}